import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AgentLayout from './AgentLayout';
import axios from 'axios';
import { useAgentAuth } from '../../context/AgentAuthContext';
import {
  TransactionsTable, TxnStatusBadge, DebitCell, CreditCell, BalanceCell, BookingLink,
} from '../../components/agent/TransactionsTable';
import { CreditCard, AlertTriangle, Calendar, Receipt, Download, Wallet } from 'lucide-react';
import { agentCreditInvoicePdfUrl, getAgentPaymentNavState } from '../../utils/agentPaymentNav';

const AgentCredit = () => {
  const { agent } = useAgentAuth();
  const [summary, setSummary] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState('');
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    (async () => {
      try {
        const [balRes, cyclesRes] = await Promise.all([
          axios.get(`${API_URL}/api/agent/credit/balance`, { withCredentials: true }),
          axios.get(`${API_URL}/api/agent/credit/billing-cycles`, { withCredentials: true }),
        ]);
        setSummary(balRes.data);
        setCycles(cyclesRes.data.billing_cycles || []);
      } catch (e) {
        console.error('Failed to load credit page', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [API_URL]);

  const fetchTxnPage = useCallback(async (params) => {
    const { data } = await axios.get(`${API_URL}/api/agent/credit/transactions`, {
      withCredentials: true,
      params: {
        page: params.page,
        limit: params.limit,
        sort: params.sort || 'desc',
        ...(params.search ? { search: params.search } : {}),
        ...(params.type ? { transaction_type: params.type } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.date_from ? { date_from: params.date_from } : {}),
        ...(params.date_to ? { date_to: params.date_to } : {}),
        ...(selectedCycle ? { billing_cycle_id: selectedCycle } : {}),
      },
    });
    return data;
  }, [API_URL, selectedCycle]);

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
        </div>
      </AgentLayout>
    );
  }

  // If the agent is CASH-mode, gently redirect them to wallet
  const creditOn = summary?.credit_enabled
    || summary?.agent_payment_capabilities?.credit_enabled
    || ['CREDIT', 'HYBRID'].includes(summary?.payment_mode);
  if (!creditOn) {
    return (
      <AgentLayout>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-sm text-amber-900"
             data-testid="agent-credit-not-enabled">
          <p className="font-semibold">Credit account is not enabled for your agency.</p>
          <p className="mt-1">
            You are currently on <strong>CASH / Wallet</strong> mode. To apply for a credit limit,
            please contact your account manager.
          </p>
        </div>
      </AgentLayout>
    );
  }

  const overdue = Number(summary.overdue_amount || 0);
  const { walletEnabled } = getAgentPaymentNavState(agent || summary);

  return (
    <AgentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">My Credit</h1>
            <p className="text-gray-600 mt-1">
              Track your credit utilisation, billing cycles, and transaction history.
            </p>
          </div>
          {walletEnabled && (
            <Link
              to="/agent/wallet"
              data-testid="agent-credit-wallet-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-800 border border-teal-200 bg-teal-50 px-4 py-2 rounded-lg"
            >
              <Wallet size={18} />
              Manage wallet
            </Link>
          )}
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <SummaryCard
            icon={<CreditCard className="text-teal-600" size={20} />}
            bg="bg-teal-50"
            label="Credit Limit"
            value={summary.credit_limit}
          />
          <SummaryCard
            icon={<CreditCard className="text-orange-600" size={20} />}
            bg="bg-orange-50"
            label="Used"
            value={summary.credit_used}
          />
          <SummaryCard
            icon={<CreditCard className="text-emerald-600" size={20} />}
            bg="bg-emerald-50"
            label="Available"
            value={summary.credit_available}
            highlight
          />
          <SummaryCard
            icon={<AlertTriangle className={overdue > 0 ? 'text-red-600' : 'text-gray-400'} size={20} />}
            bg={overdue > 0 ? 'bg-red-50' : 'bg-gray-50'}
            label="Overdue"
            value={overdue}
            valueClass={overdue > 0 ? 'text-red-700' : 'text-gray-500'}
          />
        </div>

        {/* Status strip */}
        <div className="bg-white border border-gray-100 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-700">Status:</span>
            <TxnStatusBadge status={summary.credit_status || 'ACTIVE'} />
          </div>
          {summary.payment_day && (
            <div className="flex items-center gap-2 text-gray-700">
              <Calendar size={14} className="text-gray-400" />
              Payment day: <strong>{summary.payment_day}</strong>
            </div>
          )}
          {summary.next_due_date && (
            <div className="flex items-center gap-2 text-gray-700">
              <Calendar size={14} className="text-gray-400" />
              Next due: <strong>{new Date(summary.next_due_date).toLocaleDateString('en-IN')}</strong>
            </div>
          )}
          {summary.last_payment_date && (
            <div className="text-gray-500 text-xs">
              Last payment: {new Date(summary.last_payment_date).toLocaleDateString('en-IN')}
            </div>
          )}
        </div>

        {/* Transactions table */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-800">Transaction History</h2>
          </div>
          <TransactionsTable
            testidPrefix="credit-txn"
            fetchPage={fetchTxnPage}
            exportUrl={`${API_URL}/api/agent/credit/transactions/export`}
            ledgerLabels={{
              opening: 'Opening Credit Used',
              closing: 'Closing Credit Used',
              debit:   'Utilised (Dr)',
              credit:  'Settled (Cr)',
            }}
            typeOptions={[
              { value: 'BOOKING', label: 'Flight (Utilisation)' },
              { value: 'TICKET_DEBIT', label: 'Support Ticket' },
              { value: 'PAYMENT', label: 'Payment (Settle)' },
              { value: 'REFUND', label: 'Refund' },
              { value: 'COMMISSION', label: 'Commission' },
              { value: 'ADJUSTMENT', label: 'Adjustment' },
            ]}
            statusOptions={[
              { value: 'pending', label: 'Pending' },
              { value: 'completed', label: 'Completed' },
              { value: 'failed', label: 'Failed' },
            ]}
            extraFilters={[
              <select
                key="cycle"
                value={selectedCycle}
                onChange={(e) => setSelectedCycle(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
                data-testid="credit-cycle-filter"
              >
                <option value="">All billing cycles</option>
                {cycles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.invoice_number || c.id.slice(0, 8)} · {c.cycle_start?.slice(0,10)} – {c.cycle_end?.slice(0,10)}
                  </option>
                ))}
              </select>,
            ]}
            columns={[
              { key: 'created_at', label: 'Date',
                render: (r) => (
                  <span className="text-gray-600 whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleString('en-IN',
                      { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })
                      : '—'}
                  </span>
                ),
                footer: () => <span className="text-gray-500">TOTAL</span> },
              { key: 'id', label: 'Txn ID',
                render: (r) => <span className="font-mono text-xs text-gray-700">{r.id || '—'}</span> },
              { key: 'description', label: 'Particulars',
                render: (r) => (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-gray-800">{r.description || '—'}</span>
                    {r.booking_id && <BookingLink booking_id={r.booking_id} booking={r} />}
                  </div>
                ) },
              { key: 'debit_amount', label: 'Utilised (Dr)', align: 'right',
                render: (r) => <DebitCell amount={r.debit_amount} />,
                footer: (l) => (
                  <span className="text-red-700 font-bold">
                    ₹{Number(l.total_debit || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                ) },
              { key: 'credit_amount', label: 'Settled (Cr)', align: 'right',
                render: (r) => <CreditCell amount={r.credit_amount} />,
                footer: (l) => (
                  <span className="text-emerald-700 font-bold">
                    ₹{Number(l.total_credit || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                ) },
              { key: 'balance', label: 'Credit Used', align: 'right',
                render: (r) => <BalanceCell value={r.balance ?? r.credit_after} />,
                footer: (l) => (
                  <span className="text-[#0C1D36] font-bold">
                    ₹{Number(l.closing_balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                ) },
              { key: 'status', label: 'Status', align: 'center',
                render: (r) => <TxnStatusBadge status={r.status || 'completed'} /> },
            ]}
            expandRow={(r) => (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-[10px] uppercase text-gray-400">Credit Before</p>
                  <p className="font-mono text-xs text-gray-800">
                    {r.credit_before != null ? `₹${Number(r.credit_before).toLocaleString('en-IN')}` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-gray-400">Credit After</p>
                  <p className="font-mono text-xs text-gray-800">
                    {r.credit_after != null ? `₹${Number(r.credit_after).toLocaleString('en-IN')}` : '—'}
                  </p>
                </div>
                {r.payment_mode && (
                  <div>
                    <p className="text-[10px] uppercase text-gray-400">Payment Mode</p>
                    <p className="text-gray-800 capitalize">{r.payment_mode}</p>
                  </div>
                )}
                {r.payment_reference && (
                  <div>
                    <p className="text-[10px] uppercase text-gray-400">Reference</p>
                    <p className="font-mono text-xs text-gray-800">{r.payment_reference}</p>
                  </div>
                )}
                {r.billing_cycle_id && (
                  <div>
                    <p className="text-[10px] uppercase text-gray-400">Billing Cycle</p>
                    <a
                      href={agentCreditInvoicePdfUrl(API_URL, r.billing_cycle_id)}
                      className="text-teal-600 hover:text-teal-700 inline-flex items-center gap-1 text-xs font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Download size={12} /> Download invoice
                    </a>
                  </div>
                )}
                {r.notes && (
                  <div className="md:col-span-2">
                    <p className="text-[10px] uppercase text-gray-400">Notes</p>
                    <p className="text-gray-800 text-xs">{r.notes}</p>
                  </div>
                )}
              </div>
            )}
          />
        </div>
      </div>
    </AgentLayout>
  );
};

const SummaryCard = ({ icon, bg, label, value, valueClass, highlight }) => (
  <div className={`rounded-xl p-4 border border-gray-100 ${highlight ? 'bg-gradient-to-br from-emerald-50 to-teal-50' : 'bg-white'}`}>
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center`}>{icon}</div>
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
    </div>
    <p className={`text-2xl font-bold ${valueClass || 'text-gray-900'}`}>
      ₹{Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
    </p>
  </div>
);

export default AgentCredit;
